import { useEffect, useState } from "react";
import Sidebar from "../components/dashboard/Sidebar";
import Topbar from "../components/dashboard/Topbar";
import StatsCards from "../components/dashboard/StatsCards";
import AnalyticsPanel from "../components/dashboard/AnalyticsPanel";
import ServicesTable from "../components/dashboard/ServicesTable";
import ProjectsTable from "../components/dashboard/ProjectsTable";
import DownloadsTable from "../components/dashboard/DownloadsTable";
import MessagesPanel from "../components/dashboard/MessagesPanel";

import {
  collection,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  getDocs,
  serverTimestamp
} from "firebase/firestore";

import { db } from "../firebase";

export default function Dashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [visitorsCount, setVisitorsCount] = useState(0);
  const [pageViewsCount, setPageViewsCount] = useState(0);
  const [topServices, setTopServices] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [recentPages] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      const [
        servicesSnap,
        projectsSnap,
        downloadsSnap,
        messagesSnap,
        visitorsSnap,
        pageViewsSnap,
        serviceViewsSnap
      ] = await Promise.all([
        getDocs(collection(db, "services")),
        getDocs(collection(db, "projects")),
        getDocs(collection(db, "downloads")),
        getDocs(collection(db, "messages")),
        getDocs(collection(db, "visitors")),
        getDocs(collection(db, "pageViews")),
        getDocs(collection(db, "serviceViews")),
      ]);

      setServices(servicesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setProjects(projectsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setDownloads(downloadsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMessages(messagesSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

      setVisitorsCount(visitorsSnap.size);
      setPageViewsCount(pageViewsSnap.size);

      const countByField = (docs, field) => {
        const map = new Map();
        docs.forEach((d) => {
          const value = d.data()[field] || "Unknown";
          map.set(value, (map.get(value) || 0) + 1);
        });

        return Array.from(map.entries())
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count);
      };

      setTopServices(
        countByField(serviceViewsSnap.docs, "service").slice(0, 5)
      );

      setTopPages(
        countByField(pageViewsSnap.docs, "page").slice(0, 5)
      );
    };

    loadData();
  }, []);

  // 🟢 SERVICES
  const handleAddService = async (data) => {
    await addDoc(collection(db, "services"), {
      ...data,
      createdAt: serverTimestamp(),
    });

    const snap = await getDocs(collection(db, "services"));
    setServices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const handleEditService = async (id, data) => {
    await updateDoc(doc(db, "services", id), data);

    const snap = await getDocs(collection(db, "services"));
    setServices(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm("Delete service?")) return;

    await deleteDoc(doc(db, "services", id));
    setServices((prev) => prev.filter((s) => s.id !== id));
  };

  // 🟢 PROJECTS
  const handleDeleteProject = async (id) => {
    if (!window.confirm("Delete project?")) return;

    await deleteDoc(doc(db, "projects", id));
    setProjects((prev) => prev.filter((p) => p.id !== id));
  };

  // 🟢 DOWNLOADS
  const handleDeleteDownload = async (id) => {
    if (!window.confirm("Delete file?")) return;

    await deleteDoc(doc(db, "downloads", id));
    setDownloads((prev) => prev.filter((d) => d.id !== id));
  };

  return (
    <div className="min-h-screen bg-[#08080A] text-zinc-100 flex">

      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col">

        {/* Topbar */}
        <Topbar setSidebarOpen={setSidebarOpen} />

        {/* Page Content */}
        <main className="p-3 sm:p-4 lg:p-6 space-y-6">

          <StatsCards
            visitorsCount={visitorsCount}
            servicesCount={services.length}
            projectsCount={projects.length}
            messagesCount={messages.length}
          />

          <AnalyticsPanel
            visitorsCount={visitorsCount}
            pageViewsCount={pageViewsCount}
            topServices={topServices}
            topPages={topPages}
            recentPages={recentPages}
          />

          <ServicesTable
            sectionId="services"
            services={services}
            onAdd={handleAddService}
            onEdit={handleEditService}
            onDelete={handleDeleteService}
          />

          <ProjectsTable
            sectionId="projects"
            projects={projects}
            onDelete={handleDeleteProject}
          />

          <MessagesPanel messages={messages} />

          <DownloadsTable
            sectionId="downloads"
            downloads={downloads}
            onDelete={handleDeleteDownload}
          />

        </main>
      </div>
    </div>
  );
}